// ──────────────────────────────────────────────
// GeoDrops — Pin Detail Bottom Sheet
// Neon Navigator: glass panel, no borders,
// tonal layering, gradient actions.
// ──────────────────────────────────────────────

import type { Pin } from "@/types";
import { getCategoryMeta, formatDistance, formatTimeLeft } from "@/types";
import { hapticImpact, showPopup } from "@/lib/telegram";

interface PinDetailProps {
  pin: Pin;
  isOwner: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
}

export function PinDetail({ pin, isOwner, onClose, onDelete }: PinDetailProps) {
  const meta = getCategoryMeta(pin.category);
  const timeLeft = formatTimeLeft(pin.expires_at);
  const distance = formatDistance(pin.distance_m);

  const handleDelete = async () => {
    const result = await showPopup(
      "Delete Pin",
      "Remove this pin? This can't be undone.",
      [
        { id: "delete", type: "destructive", text: "Delete" },
        { id: "cancel", type: "cancel", text: "Cancel" },
      ]
    );
    if (result === "delete") {
      hapticImpact("medium");
      onDelete(pin.id);
    }
  };

  return (
    <div
      className="glass-panel"
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        background: "rgba(8,21,66,0.92)",
        backdropFilter: "blur(24px)",
        WebkitBackdropFilter: "blur(24px)",
        borderRadius: "24px 24px 0 0",
        padding: "20px 20px calc(env(safe-area-inset-bottom, 0px) + 20px)",
        boxShadow: "0 -12px 32px rgba(0,0,0,0.4)",
        zIndex: 2000,
        animation: "slideUp 0.2s ease-out",
      }}
    >
      {/* Handle bar */}
      <div
        style={{
          width: 36,
          height: 4,
          borderRadius: 2,
          background: "#3d466c",
          margin: "0 auto 20px",
        }}
      />

      {/* Category badge + time + distance */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span
          style={{
            background: `${meta.color}20`,
            color: meta.color,
            padding: "5px 12px",
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          {meta.emoji} {meta.label}
        </span>
        <span style={{ color: "#6b739d", fontSize: 12, fontWeight: 600 }}>{timeLeft}</span>
        {distance && (
          <span style={{ color: "#6b739d", fontSize: 12, fontWeight: 600 }}>{distance}</span>
        )}
      </div>

      {/* Message */}
      <p
        style={{
          color: "#e1e4ff",
          fontSize: 17,
          lineHeight: 1.55,
          margin: "0 0 12px",
          wordBreak: "break-word",
          fontFamily: "var(--font-body, Inter, sans-serif)",
        }}
      >
        {pin.message}
      </p>

      {/* Author */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 0 20px",
        }}
      >
        <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: "#182760",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#85adff",
          fontSize: 14,
          fontWeight: 700,
        }}>
          {pin.author_name.charAt(0).toUpperCase()}
        </div>
        <span style={{ color: "#a0a9d5", fontSize: 13, fontWeight: 500 }}>
          {pin.author_name}
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 10 }}>
        <button
          onClick={onClose}
          style={{
            flex: 1,
            padding: "14px",
            borderRadius: 9999,
            border: "1px solid rgba(61,70,108,0.3)",
            background: "rgba(24,39,96,0.5)",
            backdropFilter: "blur(12px)",
            color: "#85adff",
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Close
        </button>
        {isOwner && (
          <button
            onClick={handleDelete}
            style={{
              padding: "14px 24px",
              borderRadius: 9999,
              border: "none",
              background: "rgba(255,113,108,0.15)",
              color: "#ff716c",
              fontSize: 15,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
